import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import LoginPage from "./components/LoginPage";
import MainPage from "./components/MainPage";
import AddRecord from "./components/AddRecord";
import Dashboard from "./components/Dashboard";
import ModifyRecords from "./components/ModifyRecords";
import DeleteRecord from "./components/DeleteRecord";
import Logout from "./components/Logout";
import Reset from "./components/reset";
import useDeviceDetect from "./hooks/useDeviceDetect";
import BreakdownTracker from "./components/BreakdownTracker";
import DfmsTruckDefectsTracker from "./components/DfmsTruckDefectsTracker";
import DriverViolationsTracker from "./components/DriverViolationsTracker";
import AddViolation from "./components/AddViolation";
import EditViolation from "./components/EditViolation";
import HandleViolation from "./components/HandleViolation";
import InfoViolation from "./components/InfoViolation";
import AddDfmsTruckDefect from "./components/AddDfmsTruckDefect";
import EditDfmsTruckDefect from "./components/EditDfmsTruckDefect";
import HandleDfmsTruckDefect from "./components/HandleDfmsTruckDefect";
import DriverChecklistList from "./components/DriverChecklistList";
import DriverChecklist from "./components/DriverChecklist";
import DriverChecklistView from "./components/DriverChecklistView";
import AddBreakdown from "./components/AddBreakdown";
import EditBreakdown from "./components/EditBreakdown";
import HandleBreakdown from "./components/HandleBreakdown";
import InfoBreakdown from "./components/InfoBreakdown";
import AddUser from "./components/AddUser";
import AddUserForm from "./components/AddUserForm";
import Signup from "./components/Signup";
import DriverList from "./components/DriverList";
import AddDriver from "./components/AddDriver";
import EditDriver from "./components/EditDriver";
import DriverRestDashboard from "./components/DriverRestDashboard";
import DriverDetails from "./components/DriverDetails";
import TripRegistration from "./components/TripRegistration";
import TripRegistrationForm from "./components/TripRegistrationForm";
import DeviceTracker from "./components/DeviceTracker";
import FleetMaster from "./components/FleetMaster";
import TpmsMonitoring from "./components/TpmsMonitoring";
import "./App.css";

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isMobile } = useDeviceDetect();
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("token");
      const storedUserRole = localStorage.getItem("userrole");
      console.log("Stored user role:", storedUserRole);
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const isExpired = decoded.exp < Date.now() / 1000;
          setIsAuthenticated(!isExpired);
          setUserRole(storedUserRole || decoded.userrole);
        } catch (error) {
          console.error("Error decoding token:", error);
          setIsAuthenticated(false);
        }
      } else {
        setUserRole(null);
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const ProtectedRoute = useCallback(
    ({ element }) => {
      if (!isAuthenticated) return <Navigate to="/" />;
      if (userRole === "driver_supv" && ![MainPage, TripRegistration, TripRegistrationForm, DriverChecklistList, DriverChecklist, DriverChecklistView].includes(element.type)) {
        return <Navigate to="/main" />;
      }
      return element;
    },
    [isAuthenticated, userRole]
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <div className={`app-container ${isMobile ? "mobile" : "desktop"}`}>
        <Routes>
          <Route
            path="/"
            element={<LoginPage setIsAuthenticated={setIsAuthenticated} setUserRole={setUserRole} />}
          />
          <Route path="/reset" element={<Reset />} />
          <Route
            path="/main"
            element={
              <ProtectedRoute element={<MainPage userRole={userRole} />} />
            }
          />
          <Route
            path="/add-record"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<AddRecord />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/dashboard"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<Dashboard />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/driver-rest-dashboard"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<DriverRestDashboard />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/modify-records"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<ModifyRecords />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/delete-records"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<DeleteRecord />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/breakdown-tracker"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<BreakdownTracker />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/driver-violations"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<DriverViolationsTracker />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/device-tracker"
            element={!["driver", "driver_supv"].includes(userRole) ? <ProtectedRoute element={<DeviceTracker />} /> : <Navigate to="/main" />}
          />
          <Route
            path="/dfms-truck-defects"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<DfmsTruckDefectsTracker />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route path="/add-dfms-truck-defect" element={<ProtectedRoute element={<AddDfmsTruckDefect />} />} />
          <Route path="/edit-dfms-truck-defect/:id" element={<ProtectedRoute element={<EditDfmsTruckDefect />} />} />
          <Route path="/handle-dfms-truck-defect/:id" element={<ProtectedRoute element={<HandleDfmsTruckDefect />} />} />
          <Route path="/add-violation" element={<ProtectedRoute element={<AddViolation />} />} />
          <Route path="/edit-violation/:id" element={<ProtectedRoute element={<EditViolation />} />} />
          <Route path="/handle-violation/:id" element={<ProtectedRoute element={<HandleViolation />} />} />
          <Route path="/info-violation/:id" element={<ProtectedRoute element={<InfoViolation />} />} />
          <Route
            path="/driver-checklists/*"
            element={<ProtectedRoute element={<DriverChecklistList />} />}
          />
          <Route
            path="/driver-checklists/new"
            element={<ProtectedRoute element={<DriverChecklist />} />}
          />
          <Route
            path="/driver-checklists/edit/:id"
            element={<ProtectedRoute element={<DriverChecklist />} />}
          />
          <Route
            path="/driver-checklists/view/:id"
            element={<ProtectedRoute element={<DriverChecklistView />} />}
          />
          <Route
            path="/add-breakdown"
            element={<ProtectedRoute element={<AddBreakdown />} />}
          />
          <Route
            path="/edit-breakdown/:id"
            element={<ProtectedRoute element={<EditBreakdown />} />}
          />
          <Route
            path="/handle-breakdown/:id"
            element={<ProtectedRoute element={<HandleBreakdown />} />}
          />
          <Route
            path="/info-breakdown/:id"
            element={<ProtectedRoute element={<InfoBreakdown />} />}
          />
          <Route
            path="/add-user"
            element={<ProtectedRoute element={<AddUser />} />}
          />
          <Route
            path="/add-user-form"
            element={<ProtectedRoute element={<AddUserForm />} />}
          />
          <Route
            path="/add-user-form/:id"
            element={
              <ProtectedRoute element={<AddUserForm isEditing={true} />} />
            }
          />
          <Route path="/signup" element={<Signup />} />
          <Route
            path="/logout"
            element={<Logout setIsAuthenticated={setIsAuthenticated} />}
          />
          <Route
            path="/driver-management"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<DriverList />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/add-driver"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<AddDriver />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/edit-driver/:id"
            element={
              !["driver", "driver_supv"].includes(userRole) ? (
                <ProtectedRoute element={<EditDriver />} />
              ) : (
                <Navigate to="/main" />
              )
            }
          />
          <Route
            path="/driver-details/:driverId"
            element={<ProtectedRoute element={<DriverDetails />} />}
          />
          <Route
            path="/trip-registration"
            element={<ProtectedRoute element={<TripRegistration />} />}
          />
          <Route
            path="/trip-registration/new"
            element={<ProtectedRoute element={<TripRegistrationForm />} />}
          />
          <Route
            path="/fleet-master"
            element={String(userRole || "").toLowerCase() === "admin" ? <ProtectedRoute element={<FleetMaster />} /> : <Navigate to="/main" />}
          />
          <Route
            path="/tpms-monitoring"
            element={String(userRole || "").toLowerCase() === "admin" ? <ProtectedRoute element={<TpmsMonitoring />} /> : <Navigate to="/main" />}
          />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
